import { useEffect, useRef, useState } from 'react';
import type { DealValues, RegistryDealSetup } from '../../api/registryDealTypes';
import { errorMessage, RegistryApiError } from '../../api/registryApi';
import type { SetupAttempt } from '../../domain/setup/liveSetupRules';
import { useRegistrySession } from '../../session/RegistrySession';
import { useSetupAutosave } from './useSetupAutosave';

/** Fields the user has edited and then left; their problems show without pressing Next. */
function useTouchedFields() {
  const [touched, setTouched] = useState<string[]>([]);
  const edited = useRef(new Set<string>());
  const blur = (key: string) => {
    if (!edited.current.has(key)) return;
    setTouched((current) => (current.includes(key) ? current : [...current, key]));
  };
  const markEdited = (key: string) => edited.current.add(key);
  return { touched, blur, markEdited };
}

export function useLiveSetup(setupId: string) {
  const session = useRegistrySession()!;
  const [setup, setSetup] = useState<RegistryDealSetup | null>(null);
  const [values, setValues] = useState<DealValues>({});
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState<SetupAttempt[]>([]);
  const { touched, blur, markEdited } = useTouchedFields();
  const { flush, queueChange } = useSetupAutosave(
    async (patch, completedStep, stillPending) => {
      const saved = await session.api.updateSetup(setupId, patch, completedStep);
      setSetup(saved);
      setValues((current) => ({ ...current, ...saved.values, ...stillPending }));
    },
    (failure) => setError(errorMessage(failure)),
  );

  useEffect(() => {
    session.api
      .getSetup(setupId)
      .then((loaded) => {
        setSetup(loaded);
        setValues(loaded.values);
      })
      .catch((failure) => setError(errorMessage(failure)));
  }, [session, setupId]);

  const change = (key: string, value: string | boolean) => {
    setValues((current) => ({ ...current, [key]: value }));
    setFieldErrors(({ [key]: _cleared, ...rest }) => rest);
    markEdited(key);
    if (typeof value === 'boolean') blur(key);
    queueChange(key, value);
  };

  const attempt = (kind: SetupAttempt) =>
    setAttempts((current) => (current.includes(kind) ? current : [...current, kind]));

  const action = async (run: () => Promise<RegistryDealSetup>) => {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await flush();
      const updated = await run();
      setSetup(updated);
      setValues(updated.values);
    } catch (failure) {
      setError(errorMessage(failure));
      if (failure instanceof RegistryApiError) setFieldErrors(failure.fields ?? {});
    } finally {
      setBusy(false);
    }
  };

  return {
    session,
    setup,
    values,
    error,
    fieldErrors,
    busy,
    attempts,
    touched,
    change,
    blur,
    attempt,
    action,
  };
}
